import { useSyncExternalStore, type ReactElement } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import "../../ui/kit/styles.ts";
import "../loading-screen.css";
import "./game-menu.css";
import "../feedback/feedback.css";
import "../look-preview/look-panel.css";
import { KitInputProvider, type KitInput } from "../../ui/kit/index.ts";
import { GameMenu, type AudioSetting, type AutoLoot, type BarKeys, type BarLock, type BarPaging, type BarReset, type ChatSizeToggle, type ChatTextToggle, type CompassPlaces, type EmotesOthers, type FrameLimitSetting, type HealthNumbers, type IdleMarks, type MotionSetting, type NameplateSetting, type PartyInvitesSetting, type PerformanceToggle, type RenderSetting, type ShowCoordinates, type ZoomSetting } from "./game-menu.tsx";
import { pageKeys } from "../controls/page.ts";
import { pageRun } from "../run/page.ts";
import type { FeedbackAccess, FeedbackDraft, SendOutcome } from "../feedback/send.ts";
import type { KeysSetting, RunSetting } from "./settings-page.ts";
import { ticksToMs } from "./leave-words.ts";
import { LOOK_CONFIRM_REST_MS, type LookScreenPage } from "./look-screen.ts";
import { gameMenuReducer, initialGameMenuState, type EmotesOffer, type FeedbackEntry, type GameMenuEvent, type GameMenuState, type GameMenuView } from "./state.ts";

/**
 * The game page's game menu (P1.9-T14): a React root of its own in the page's `#menu` host, over the HUD and
 * under the confirm dialog's `#modal`, on the page's input router. It keeps the menu's state (`state.ts`),
 * draws it only when it changes, and runs what an item asks of the page: Change character and Settings' Log out
 * everywhere, which ask through the page's confirm dialog first, and Log out. Each runs once, as the state starts
 * leaving by it. It also keeps the Feedback entry (ADR-081's amendment of 2026-10-05): the kinds the gateway
 * offered, the account's access as the gateway or a refused send named it, and a timer that opens a paused entry
 * again when its pause ends while the page is open. And it keeps the temporary Change look screen's two facts
 * (ADR-094's amendment of 2026-10-06, B10 and B5): whether the page's freeze notice stands, asked whenever the page's
 * notices change and whenever the menu is asked for, and the time before which Confirm rests after an accepted
 * change.
 */

/**
 * The longest delay a browser's timer takes, in milliseconds; a pause past it is waited for in parts.
 */
const MAX_TIMER_MS = 0x7fffffff;

/**
 * How often the open menu looks whether its character is in combat with no lock known, and the least time between
 * two questions to the gateway about it, in milliseconds. The gateway answers one `leave` a second (ADR-084's
 * amendment of 2026-10-13, item 9), and a confirmed leave shares that window, so the menu asks at half that rate.
 */
const LOCK_WATCH_MS = 250;
const LOCK_ASK_MS = 2000;

/**
 * What the menu needs to send feedback: what the form may send as the page starts, and the send itself, which
 * never rejects.
 */
export interface GameMenuFeedback {
  /** `all` for a destination that takes both kinds from the start (the dev server), `bug` until the gateway offers more. */
  readonly kinds: FeedbackEntry["kinds"];
  /**
   * Sends one report.
   *
   * @param draft What the form holds.
   * @returns The reference, or the sentence to show and any access the answer named.
   */
  readonly send: (draft: FeedbackDraft) => Promise<SendOutcome>;
  /** The page's clock in milliseconds; `Date.now` unless given. */
  readonly now?: () => number;
}

/**
 * What the menu needs from the page. `changeCharacter`, `logOut` and `logOutEverywhere` never reject: the page's
 * account requests answer every failure with a sentence rather than an exception.
 */
export interface GameMenuPage {
  /** The page's input router. */
  readonly input: KitInput;
  /** The page's world zoom, which Settings moves on Apply, as it does each setting below (P1.9-T44). */
  readonly zoom: ZoomSetting;
  /** The page's render resolution, which Settings changes (ADR-093 decision 10); without it Settings offers none. */
  readonly render?: RenderSetting;
  /** The page's frame limit, which Settings changes (ADR-093 decisions 40 to 45); without it Settings offers none. */
  readonly frameLimit?: FrameLimitSetting | undefined;
  /** The page's Nameplates choices, which Settings changes (P1.9-T24). */
  readonly nameplates: NameplateSetting;
  /** The page's health numbers setting, which Settings changes (ADR-061's amendment "the player card"). */
  readonly healthNumbers: HealthNumbers;
  /** The page's Show idle and AFK marks choice, which Settings changes (P1.9-T45). */
  readonly idleMarks: IdleMarks;
  /** The page's Show key numbers on the bar choice, which Settings changes (P2.18-T15). */
  readonly barKeys: BarKeys;
  /** The page's Ability bar choice, which Settings changes (P1.9-T133). */
  readonly barPaging: BarPaging;
  /** The page's Lock the ability bar choice, which Settings changes (P2.8-T29); without it Settings offers none. */
  readonly barLock?: BarLock;
  /** The page's Reset ability bar, which Settings confirms and runs (P2.8-T29); without it the row's button rests. */
  readonly barReset?: BarReset;
  /** The page's Party invitations setting, which Settings changes (P2.13-T16); without it Settings has no Social section. */
  readonly partyInvites?: PartyInvitesSetting | undefined;
  /** The page's Show other players' emotes choice, which Settings changes (P2.19-T7). */
  readonly emotesOthers: EmotesOthers;
  /** The page's Show coordinates choice (P1.9-T197), on a page that has a minimap. */
  readonly coordinates?: ShowCoordinates;
  /** The page's Places on the compass choice (P1.9-T189), on a page that has a minimap. */
  readonly compassPlaces?: CompassPlaces;
  /** Auto loot (P2.8-T79), on a page that has it. */
  readonly autoLoot?: AutoLoot;
  /** The page's chat size, which Settings changes and the chat panel's grip does too (P1.9-T42). */
  readonly chatSize: ChatSizeToggle;
  /** The page's chat text size, which Settings changes (P1.9-T224). */
  readonly chatText: ChatTextToggle;
  /** The page's performance panel, which Settings shows and hides as F9 does (ADR-087); without it Settings offers none. */
  readonly performance?: PerformanceToggle;
  /** The page's reduced motion, which Settings changes (R-0061). */
  readonly motion: MotionSetting;
  /** The page's sound, whose levels Settings' Audio section previews and, on Apply, keeps (ADR-085). */
  readonly audio: AudioSetting;
  /** The key table Settings' Controls section changes (P1.9-T50); the page's own unless given. */
  readonly keys?: KeysSetting;
  /** The Run setting Settings' Controls section changes (ADR-116 decision 11); the page's own unless given. */
  readonly run?: RunSetting;
  /**
   * Told whenever the surface shown changes, so the page can sound a window opening or closing (ADR-085).
   *
   * @param before The surface shown before.
   * @param after The surface shown now.
   */
  readonly onView?: (before: GameMenuView, after: GameMenuView) => void;
  /**
   * Opens the Inventory window (P2.8-T11), called once the menu has closed on its Inventory item; a page without a
   * bag gives none and the item only closes the menu.
   */
  readonly onInventory?: () => void;
  /**
   * Opens the character window (ADR-138's amendment of 2026-10-31, decision 1), called once the menu has closed on its
   * Character item; a page without one gives none and the item only closes the menu.
   */
  readonly onCharacter?: () => void;
  /**
   * The emote wheel as the menu's Emotes item needs it (P2.19-T8, ADR-114 decision 1.5): what the menu shows of it,
   * a way to hear that change, and a way to open it, called once the menu has closed on the item. A page without a
   * wheel gives none and the menu has no Emotes item.
   */
  readonly emotes?: { readonly offer: () => EmotesOffer; readonly watch: (listener: () => void) => () => void; readonly open: () => void } | undefined;
  /** Whether the page plays an account session; a `?token=` dev session has nothing to log out of. */
  readonly account: boolean;
  /** What the temporary Change look screen needs (`look-screen.ts`); without it the menu has no Change look entry. */
  readonly look?: LookScreenPage | undefined;
  /** Where the page sends feedback; without it the menu has no Feedback entry. */
  readonly feedback?: GameMenuFeedback | undefined;
  /**
   * Asks the player to confirm changing character and, once they do, departs the world for character select
   * (P1.9-T62), concealing the menu as it goes.
   *
   * @returns True when the page departed the world, false when the player stayed.
   */
  readonly changeCharacter: () => Promise<boolean>;
  /**
   * Ends this tab's session and returns the page to the log in form.
   *
   * @returns True when the page is leaving, false when a fight refused the leave and the player stays.
   */
  readonly logOut: () => Promise<boolean>;
  /**
   * Asks the player to confirm logging out everywhere and, once they do, ends every session of the account.
   *
   * @returns True when the page is leaving for the log in form, false when the player stayed.
   */
  readonly logOutEverywhere: () => Promise<boolean>;
  /**
   * The fight that locks Change character and Log out (P2.3-T4, ADR-084's amendment of 2026-10-13): whether the
   * page's character is in combat now, as its HUD shows, and a question to the gateway that answers with the ticks
   * the lock has left, or null when there was no answer. Without it the menu never locks the two items by itself.
   */
  readonly leaveLock?: { readonly inCombat: () => boolean; readonly ask: () => Promise<number | null> } | undefined;
  /**
   * Called once the menu has closed by itself while the page is out of the world (P1.9-T15): the player
   * cancelled logging out everywhere after the page had left the world, and the busy menu and Settings closed
   * with the confirm, leaving focus on nothing, so the page hands it to what now stands in front, the loading shell's
   * notice.
   */
  readonly onClosedWhileConcealed?: () => void;
}

/**
 * The menu as the page drives it.
 */
export interface GameMenuControl {
  /** Tells the menu the world is revealed, so a request opens it from now on. */
  reveal(): void;
  /**
   * Tells the menu the page left the world (P1.9-T15): it closes, Settings with it, before this returns, so
   * nothing it gives focus back to as it goes can take focus from the notice that follows, and opens nothing
   * until the next reveal.
   */
  conceal(): void;
  /** Asks for the menu: Escape with no layer open, or the menu button. */
  request(): void;
  /** Hears the gateway's `feedbackOffer`: the form may send a feature request too. */
  offerFeedback(): void;
  /**
   * Hears the account's access to feedback, from the gateway's `feedbackAccess` or a refused send: the entry is
   * disabled while it is paused or off, and opens again by itself when a pause ends.
   *
   * @param access The access, a pause's end on the page's clock.
   */
  feedbackAccess(access: FeedbackAccess): void;
  /**
   * Tells the menu a fight locks Change character and Log out for a number of ticks, as the gateway answered a
   * leave it refused.
   *
   * @param ticks The ticks the lock has left.
   */
  locked(ticks: number): void;
  /** The menu's state. */
  readonly state: GameMenuState;
}

/**
 * Mounts the game menu in its host, closed until the world is revealed and the player asks for it.
 *
 * @param host The page's `#menu` element in the overlay root, positioned over the page and taking no pointer
 *   events itself; the menu's scrim fills it and takes them while the menu is open.
 * @param page What the menu needs from the page.
 * @returns The menu.
 * @example
 * const menu = mountGameMenu(document.getElementById("menu") as HTMLElement, { input, zoom, nameplates, healthNumbers, idleMarks, emotesOthers, barKeys, barPaging, chatText, motion: PAGE_MOTION, account: true, changeCharacter, logOut, logOutEverywhere });
 * input.onMenu(() => menu.request());
 */
export function mountGameMenu(host: HTMLElement, page: GameMenuPage): GameMenuControl {
  const clock = page.feedback?.now ?? Date.now;
  const lookClock = page.look?.now ?? Date.now;
  let lookRestUntil = 0;
  let state = initialGameMenuState(page.account, page.feedback === undefined ? null : { kinds: page.feedback.kinds, access: { state: "open" } }, page.look !== undefined);
  let pauseTimer: ReturnType<typeof setTimeout> | null = null;
  let lockTimer: ReturnType<typeof setTimeout> | null = null;
  let lockWatch: ReturnType<typeof setInterval> | null = null;
  let asking = false;
  let asked = Number.NEGATIVE_INFINITY;
  const listeners = new Set<() => void>();
  const root = createRoot(host);

  /**
   * Adds a listener for the state.
   *
   * @param listener Called when the state changes.
   * @returns A function that removes it.
   */
  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  /**
   * The state React draws.
   *
   * @returns The state.
   */
  const snapshot = (): GameMenuState => state;

  /**
   * Moves the menu by one event, tells the view when it changed, and starts what the tab leaves by as the
   * state starts leaving: logging out at once, or asking to change character or to log out everywhere, either of
   * which lets the menu stay when the player cancels. A cancel after the page left the world closes the menu
   * instead (`state.ts`), drawn at once so it has let focus go before the page is told.
   *
   * @param event What happened.
   */
  const dispatch = (event: GameMenuEvent): void => {
    const before = state;
    const next = gameMenuReducer(before, event);
    if (next === before) {
      return;
    }
    state = next;
    for (const listener of listeners) {
      listener();
    }
    if (before.view !== next.view) {
      page.onView?.(before.view, next.view);
      watchLock();
    }
    if (event.type === "chosen" && event.item === "inventory") {
      page.onInventory?.();
    }
    if (event.type === "chosen" && event.item === "character") {
      page.onCharacter?.();
    }
    if (event.type === "chosen" && event.item === "emotes") {
      page.emotes?.open();
    }
    if (event.type === "lookChanged") {
      lookRestUntil = lookClock() + LOOK_CONFIRM_REST_MS;
    }
    if (before.leaving !== null) {
      return;
    }
    if (next.leaving !== null) {
      void (next.leaving === "change" ? page.changeCharacter() : next.leaving === "logout" ? page.logOut() : page.logOutEverywhere()).then((leaving) => {
        if (leaving) {
          return;
        }
        flushSync(() => dispatch({ type: "stayed" }));
        if (!state.revealed && state.view === "closed") {
          page.onClosedWhileConcealed?.();
        }
      });
    }
  };

  /**
   * Asks the page whether its freeze notice stands and tells the menu.
   */
  const readFrozen = (): void => {
    if (page.look !== undefined) {
      dispatch({ type: "frozen", frozen: page.look.frozen() });
    }
  };

  /**
   * Takes the account's access and, for a pause, arms the timer that opens the entry again at its end; a pause
   * longer than a timer can wait is waited for in parts.
   *
   * @param access The access.
   */
  const takeAccess = (access: FeedbackAccess): void => {
    if (pauseTimer !== null) {
      clearTimeout(pauseTimer);
      pauseTimer = null;
    }
    const left = access.state === "paused" ? access.until - clock() : 0;
    if (access.state === "paused" && left <= 0) {
      dispatch({ type: "feedbackAccess", access: { state: "open" } });
      return;
    }
    dispatch({ type: "feedbackAccess", access });
    if (access.state === "paused") {
      pauseTimer = setTimeout(() => takeAccess(access), Math.min(left, MAX_TIMER_MS));
    }
  };

  /**
   * Sends a report for the form and takes any access its answer named, so the entry follows a refusal.
   *
   * @param draft What the form holds.
   * @returns The outcome, as the page's send answered it.
   */
  const send = async (draft: FeedbackDraft): Promise<SendOutcome> => {
    const feedback = page.feedback;
    if (feedback === undefined) {
      throw new Error("the game menu has no feedback destination");
    }
    const outcome = await feedback.send(draft);
    if (!outcome.ok && outcome.access !== null) {
      takeAccess(outcome.access);
    }
    return outcome;
  };

  /**
   * The menu, following the state.
   *
   * @returns The menu inside the router's provider.
   */
  function View(): ReactElement {
    const current = useSyncExternalStore(subscribe, snapshot, snapshot);
    return (
      <KitInputProvider input={page.input}>
        <GameMenu state={current} zoom={page.zoom} render={page.render} frameLimit={page.frameLimit} nameplates={page.nameplates} healthNumbers={page.healthNumbers} idleMarks={page.idleMarks} emotesOthers={page.emotesOthers} {...(page.coordinates === undefined ? {} : { coordinates: page.coordinates })} {...(page.compassPlaces === undefined ? {} : { compassPlaces: page.compassPlaces })} {...(page.autoLoot === undefined ? {} : { autoLoot: page.autoLoot })} barKeys={page.barKeys} barPaging={page.barPaging} barLock={page.barLock} barReset={page.barReset} partyInvites={page.partyInvites} chatSize={page.chatSize} chatText={page.chatText} motion={page.motion} audio={page.audio} keys={page.keys ?? pageKeys} run={page.run ?? pageRun} performance={page.performance} onSend={page.feedback === undefined ? undefined : send} now={clock} look={page.look} lookRestUntil={lookRestUntil} onEvent={dispatch} />
      </KitInputProvider>
    );
  }

  /**
   * Asks the page what the menu shows of the emote wheel and tells the menu.
   */
  const readEmotes = (): void => {
    if (page.emotes !== undefined) {
      dispatch({ type: "emotes", emotes: page.emotes.offer() });
    }
  };

  /**
   * Takes the ticks a fight's lock has left: the two items are locked until then on the page's clock, and a timer
   * lifts the lock at its end, after which the watch asks again if the fight goes on.
   *
   * @param ticks The ticks left, 0 or less for none.
   */
  const takeLock = (ticks: number): void => {
    if (lockTimer !== null) {
      clearTimeout(lockTimer);
      lockTimer = null;
    }
    if (ticks <= 0) {
      dispatch({ type: "lock", until: null });
      return;
    }
    const waitMs = ticksToMs(ticks);
    dispatch({ type: "lock", until: clock() + waitMs });
    lockTimer = setTimeout(() => {
      lockTimer = null;
      dispatch({ type: "lock", until: null });
      readLock();
    }, waitMs);
  };

  /**
   * Asks the gateway how long the fight locks leaving, when the menu is open, the character is in combat, no lock is
   * known and no question is out; at most once in `LOCK_ASK_MS`, under the gateway's own limit on the question.
   */
  const readLock = (): void => {
    const lock = page.leaveLock;
    if (lock === undefined || state.view === "closed" || state.leaving !== null || state.lock !== null || asking || !lock.inCombat() || clock() - asked < LOCK_ASK_MS) {
      return;
    }
    asking = true;
    asked = clock();
    void lock.ask().then((ticks) => {
      asking = false;
      if (ticks !== null) {
        takeLock(ticks);
      }
    });
  };

  /**
   * Starts or stops the watch that asks for the lock while the menu is open: a fight may start, or go on past a lock
   * that ran out, while the menu shows.
   */
  const watchLock = (): void => {
    const open = page.leaveLock !== undefined && state.view !== "closed";
    if (open && lockWatch === null) {
      lockWatch = setInterval(readLock, LOCK_WATCH_MS);
      readLock();
    } else if (!open && lockWatch !== null) {
      clearInterval(lockWatch);
      lockWatch = null;
    }
  };

  page.look?.watchFrozen(readFrozen);
  page.emotes?.watch(readEmotes);
  root.render(<View />);
  return {
    reveal: () => dispatch({ type: "revealed" }),
    conceal: () => flushSync(() => dispatch({ type: "concealed" })),
    request: () => {
      readFrozen();
      readEmotes();
      dispatch({ type: "requested" });
    },
    offerFeedback: () => dispatch({ type: "feedbackOffered" }),
    feedbackAccess: takeAccess,
    locked: takeLock,
    get state(): GameMenuState {
      return state;
    },
  };
}
